import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Instagram, Facebook, ArrowLeft, ArrowDown, Search, StickyNote, Link2, Sparkles } from 'lucide-react';
import { useMetaMessagingStore, type MetaConversation, type MetaMessage } from '@/lib/stores/metaMessagingStore';
import { MetaComposer } from './MetaComposer';
import { MetaClientLink } from './MetaClientLink';
import { MetaMessageBubble } from './MetaMessageBubble';
import { conversationLabel, CHANNEL_LABEL, dayKey, dayLabel } from './metaConversationHelpers';
import { MetaConversationTools } from './MetaConversationTools';
import { ChatAvatar } from '@/components/whatsapp2/chat/ChatAvatar';
import { MessageSearchBar } from '@/components/whatsapp2/chat/MessageSearchBar';
import { iconTip } from '@/components/messages/ChatTooltip';
import { MetaNotesPanel } from './MetaNotesPanel';
import { MetaAssistantPanel } from './MetaAssistantPanel';
import { lastMessageIsFromContact, referralLabel, searchThread } from './metaMessagingRules';

interface MetaThreadProps {
  conversation: MetaConversation;
  canSend: boolean;
  linkedClientName?: string | null;
  onBack?: () => void;
  /**
   * Drawer compatto: il pannello assistente si apre dal pulsante in alto (come
   * WhatsApp). Altrimenti e' sempre a destra, chiudibile (chiuso sotto i 1440px).
   */
  compact?: boolean;
}

const BOTTOM_THRESHOLD = 80;

/**
 * Thread di una conversazione Meta: intestazione con foto, canale e
 * collegamento cliente; bolle con spunte e allegati in linea; composer.
 * Scorrimento: se sei in fondo, un messaggio nuovo scorre morbido; se stai
 * leggendo piu' su, compare la pillola "nuovi messaggi" senza strapparti via.
 */
export function MetaThread({ conversation, canSend, linkedClientName, onBack, compact = false }: MetaThreadProps) {
  const messages = useMetaMessagingStore((s) => s.messagesByConversation[conversation.id]);
  const fetchMessages = useMetaMessagingStore((s) => s.fetchMessages);
  const markRead = useMetaMessagingStore((s) => s.markRead);
  const retryMessage = useMetaMessagingStore((s) => s.retryMessage);
  const discardFailedMessage = useMetaMessagingStore((s) => s.discardFailedMessage);
  const setReplyTo = useMetaMessagingStore((s) => s.setReplyTo);
  const reactToMessage = useMetaMessagingStore((s) => s.reactToMessage);
  const sendSenderAction = useMetaMessagingStore((s) => s.sendSenderAction);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);
  const [notesOpen, setNotesOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const seenSentRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const [pendingNew, setPendingNew] = useState(0);
  const lastCountRef = useRef(0);
  const lastConversationRef = useRef<string | null>(null);
  const Icon = conversation.channel === 'instagram' ? Instagram : Facebook;
  const label = conversationLabel(conversation, linkedClientName);

  // Conversazione aperta e nuovo messaggio in arrivo (la riga si aggiorna dal realtime):
  // resta letta. Senza, il badge del canale si riaccendeva con la chat davanti agli occhi.
  useEffect(() => {
    if (conversation.last_inbound_at) markRead(conversation.id).catch(() => undefined);
  }, [conversation.id, conversation.last_inbound_at, markRead]);

  useEffect(() => {
    if (!messages) fetchMessages(conversation.id).catch(() => undefined);
  }, [conversation.id, messages, fetchMessages]);

  // Cambio conversazione: ricerca e note chiuse.
  useEffect(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setMatchIndex(0);
    setNotesOpen(false);
  }, [conversation.id]);

  // "Visualizzato" verso il contatto: una volta per apertura, solo se l'ultimo messaggio
  // e' suo e solo per chi puo' rispondere (il core applica le stesse barriere dell'invio).
  useEffect(() => {
    if (!canSend || !messages || seenSentRef.current === conversation.id) return;
    seenSentRef.current = conversation.id;
    if (lastMessageIsFromContact(messages)) void sendSenderAction(conversation.id, 'mark_seen');
  }, [canSend, messages, conversation.id, sendSenderAction]);

  // Ricerca nel thread: risultati dal piu' recente; il selezionato viene portato in vista.
  const matches = useMemo(() => (searchOpen ? searchThread(messages ?? [], searchQuery) : []), [searchOpen, messages, searchQuery]);
  const activeMatchId = matches.length > 0 ? matches[Math.min(matchIndex, matches.length - 1)] : null;
  useEffect(() => {
    setMatchIndex(0);
  }, [searchQuery]);
  useEffect(() => {
    if (!activeMatchId) return;
    document.getElementById(`meta-msg-${activeMatchId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [activeMatchId]);

  const scrollToBottom = useCallback((smooth: boolean) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nowAtBottom = distance < BOTTOM_THRESHOLD;
    setAtBottom(nowAtBottom);
    if (nowAtBottom) setPendingNew(0);
  };

  // Nuovo thread: in fondo subito, senza animazione. Messaggi nuovi: scorrimento
  // morbido se eri in fondo, altrimenti pillola.
  useEffect(() => {
    const count = messages?.length ?? 0;
    if (lastConversationRef.current !== conversation.id) {
      lastConversationRef.current = conversation.id;
      lastCountRef.current = count;
      setPendingNew(0);
      setAtBottom(true);
      requestAnimationFrame(() => scrollToBottom(false));
      return;
    }
    // Primo caricamento del thread (da "non caricato" a N messaggi): non sono
    // messaggi nuovi, si va in fondo senza pillola.
    if (lastCountRef.current === 0 && count > 0) {
      lastCountRef.current = count;
      setPendingNew(0);
      requestAnimationFrame(() => scrollToBottom(false));
      return;
    }
    if (count > lastCountRef.current) {
      const added = count - lastCountRef.current;
      lastCountRef.current = count;
      const lastMine = messages?.[count - 1]?.direction === 'outbound';
      // Posizione letta dal DOM al momento dell'arrivo: lo stato `atBottom` puo'
      // essere in ritardo di un render rispetto a uno scorrimento appena fatto.
      const el = scrollRef.current;
      const nearBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD + 40 : atBottom;
      if (nearBottom || lastMine) {
        requestAnimationFrame(() => scrollToBottom(true));
      } else {
        setPendingNew((n) => n + added);
      }
      // Arrivato mentre il thread e' aperto: letto.
      if (!lastMine) markRead(conversation.id).catch(() => undefined);
    } else {
      lastCountRef.current = count;
    }
  }, [messages, conversation.id, atBottom, scrollToBottom, markRead]);

  const byMid = React.useMemo(() => {
    const map = new Map<string, MetaMessage>();
    for (const m of messages ?? []) if (m.meta_message_id) map.set(m.meta_message_id, m);
    return map;
  }, [messages]);

  const referral = referralLabel(conversation);

  return (
    <div className="h-full flex min-w-0 bg-white dark:bg-[#191919] animate-fade-in">
    <div className="relative flex-1 min-w-0 h-full flex flex-col">
      <div className="flex items-center gap-3 px-4 h-12 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#191919]">
        {onBack && (
          <button type="button" onClick={onBack} className="lg:hidden p-1 rounded hover:bg-[rgba(55,53,47,0.06)]" aria-label="Indietro">
            <ArrowLeft className="h-4 w-4 text-[#787774]" />
          </button>
        )}
        <ChatAvatar name={label} imageUrl={conversation.profile_pic_url || undefined} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-[#37352F] dark:text-white truncate">{label}</div>
          <div className="text-[11px] text-[#a4a4a0] inline-flex items-center gap-1">
            <Icon className="h-3 w-3" />
            {CHANNEL_LABEL[conversation.channel]}
            {conversation.profile_username && <span>· @{conversation.profile_username}</span>}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setSearchOpen((v) => !v)}
          className={`p-1.5 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 ${searchOpen ? 'bg-[rgba(55,53,47,0.08)]' : ''}`}
          {...iconTip('Cerca nei messaggi')}
        >
          <Search className="h-4 w-4 text-[#787774]" />
        </button>
        <button
          type="button"
          onClick={() => setNotesOpen((v) => !v)}
          className={`p-1.5 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 ${notesOpen ? 'bg-[rgba(55,53,47,0.08)]' : ''}`}
          {...iconTip('Note interne sulla conversazione')}
        >
          <StickyNote className="h-4 w-4 text-[#787774]" />
        </button>
        {compact && (
          <button
            type="button"
            onClick={() => setAssistantOpen((v) => !v)}
            className={`hidden md:inline-flex p-1.5 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 ${assistantOpen ? 'bg-[rgba(55,53,47,0.08)]' : ''}`}
            aria-pressed={assistantOpen}
            {...iconTip(assistantOpen ? 'Chiudi il pannello assistente' : 'Apri il pannello assistente: Lead/Cliente, Task commerciale, Chiedi all\'AI')}
          >
            <Sparkles className="h-4 w-4 text-[#787774]" />
          </button>
        )}
        <MetaClientLink conversation={conversation} canEdit={canSend} />
      </div>
      {searchOpen && (
        <MessageSearchBar
          query={searchQuery}
          onChange={setSearchQuery}
          totalResults={matches.length}
          currentIndex={Math.min(matchIndex, Math.max(matches.length - 1, 0))}
          onPrev={() => setMatchIndex((i) => (matches.length ? (i - 1 + matches.length) % matches.length : 0))}
          onNext={() => setMatchIndex((i) => (matches.length ? (i + 1) % matches.length : 0))}
          onClose={() => {
            setSearchOpen(false);
            setSearchQuery('');
          }}
        />
      )}
      <div className="px-4 py-1.5 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 bg-white dark:bg-[#191919]">
        <MetaConversationTools conversation={conversation} />
      </div>

      <div className="relative flex-1 min-h-0">
        <div ref={scrollRef} onScroll={handleScroll} className="h-full overflow-y-auto px-4 py-4 space-y-2 whatsapp-chat-bg">
          {!messages && (
            <div className="space-y-3 animate-pulse" aria-label="Caricamento messaggi">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={`flex ${i % 2 ? 'justify-end' : 'justify-start'}`}>
                  <div className={`h-10 rounded-xl bg-[rgba(55,53,47,0.08)] dark:bg-white/10 ${i % 2 ? 'w-2/5' : 'w-3/5'}`} />
                </div>
              ))}
            </div>
          )}
          {referral && (
            <div className="flex justify-center">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/90 dark:bg-gray-800 text-[11px] text-[#54656f] dark:text-gray-300 shadow-sm" {...(conversation.referral_at ? iconTip(`Ricevuto il ${new Date(conversation.referral_at).toLocaleString('it-IT')}`) : {})}>
                <Link2 className="h-3 w-3" /> {referral}
              </span>
            </div>
          )}
          {messages && messages.length === 0 && (
            <p className="text-sm text-[#787774] text-center py-6">Nessun messaggio in questa conversazione</p>
          )}
          {messages?.map((m, i) => {
            const prev = messages[i - 1];
            const showDay = !prev || dayKey(prev.sent_at) !== dayKey(m.sent_at);
            return (
              <React.Fragment key={m.id}>
                {showDay && (
                  <div className="flex justify-center py-1">
                    <span className="px-2.5 py-0.5 rounded-md bg-white/90 dark:bg-gray-800 text-[11px] text-[#54656f] dark:text-gray-300 shadow-sm">
                      {dayLabel(m.sent_at)}
                    </span>
                  </div>
                )}
                <div id={`meta-msg-${m.id}`}>
                  <MetaMessageBubble
                    message={m}
                    channel={conversation.channel}
                    searchQuery={searchOpen ? searchQuery : undefined}
                    activeMatch={m.id === activeMatchId}
                    quoted={m.reply_to_mid ? byMid.get(m.reply_to_mid) ?? null : null}
                    onRetry={() => void retryMessage(conversation.id, m.id)}
                    onDiscard={() => discardFailedMessage(conversation.id, m.id)}
                    onReply={canSend ? () => setReplyTo(conversation.id, m) : undefined}
                    onReact={canSend && m.direction === 'inbound' ? (r) => void reactToMessage(conversation.id, m.id, r) : undefined}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
        {pendingNew > 0 && (
          <button
            type="button"
            onClick={() => {
              scrollToBottom(true);
              setPendingNew(0);
            }}
            className="absolute left-1/2 -translate-x-1/2 bottom-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#2383e2] text-white text-xs font-medium shadow-lg animate-slide-up"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {pendingNew === 1 ? '1 nuovo messaggio' : `${pendingNew} nuovi messaggi`}
          </button>
        )}
      </div>

      <MetaComposer conversation={conversation} canSend={canSend} />
      <MetaNotesPanel
        conversationId={conversation.id}
        organizationId={conversation.organization_id}
        clientId={conversation.client_id ?? null}
        clientName={linkedClientName}
        isOpen={notesOpen}
        onClose={() => setNotesOpen(false)}
      />
    </div>
      {(!compact || assistantOpen) && (
        <div className="hidden md:flex h-full">
          <MetaAssistantPanel
            key={compact ? 'compact' : 'full'}
            conversation={conversation}
            linkedClientName={linkedClientName}
            canSend={canSend}
            onOpenNotes={() => setNotesOpen(true)}
            rememberCollapse={!compact}
          />
        </div>
      )}
    </div>
  );
}
